import React, { useCallback, useMemo } from "react";
import {
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Box,
  Typography,
  IconButton,
  MenuItem,
  TextField,
  Chip,
  Button,
  Stack,
} from "@mui/material";
import { useState } from "react";
import { Badge } from "react-bootstrap";
import PaymentDrawer from "../../payments/PaymentDrawer";
import { ContentCopy, Visibility, Close } from "@mui/icons-material";
import { toast } from "react-toastify";
import { formatPayableDate } from "../../../utils/payableDate";
import SearchField, { filterSelectSx } from "../../common/SearchField";
import { displayPlatformName } from "../../common/displayPlatformName";

const STYLING_TABLE = {
  fontWeight: 700,
  fontFamily: "Quicksand",
  fontSize: "12px",
};

const BADGE_BASE_STYLE = {
  fontFamily: "Quicksand",
  color: "#FFFFFF",
  padding: "4px 8px",
  fontSize: "11px",
  borderRadius: "4px",
  fontWeight: 600
};

const TABLE_CELL_STYLE = {
  fontSize: "11px",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  fontFamily: "Quicksand"
}

// Sentinel for "no POC assigned" — '' is already the dropdown's "All POCs".
const UNASSIGNED_POC = "__unassigned__";

// This table's POC comes from createdby, not the pocData the Payment Queue
// tab uses, but the grouping rule is the same: rows only carry the name, so
// that is what options are keyed on, and two POCs sharing a name group
// together rather than producing two identical-looking entries.
const pocKeyOf = (item) => (item?.createdby?.fullName || "").trim();

const showDeleteAndCopy = (status, isDisable) => {
  // Only show for "pending" status
  return ["P", "pending", "Pending"].includes(status) && !isDisable;
};

const getPlatformNames = (platforms) => {
  if (!platforms || !Array.isArray(platforms)) return "-";
  return platforms.map((p) => displayPlatformName(p.name)).join(" ");
};

const convertDate = (id) => {
  const date = new Date(
    parseInt(id.substr(0, 8), 16) * 1000,
  ).toLocaleDateString();
  return date;
}

function StatusTable({ campaignInfo, statusType }) {
  const [drawerOpen, setDrawerOpen] = useState(false);

  // State to store the selected item data
  const [selectedItem, setSelectedItem] = useState(null);

  const tableData = useMemo(() => {
    if (statusType === "ClubLinks") {
      if (Array.isArray(campaignInfo)) {
        return campaignInfo;
      }
      if (campaignInfo?.details) {
        return campaignInfo.details;
      }
    }

    return campaignInfo || [];
  }, [campaignInfo, statusType]);

  const [pocFilter, setPocFilter] = useState("");
  const [searchText, setSearchText] = useState("");

  const pocOptions = useMemo(() => {
    const names = new Set();
    tableData.forEach((item) => {
      const name = pocKeyOf(item);
      if (name) names.add(name);
    });
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }, [tableData]);

  // Only offered when such rows exist, so it can't be a choice that only
  // empties the table.
  const hasUnassignedPoc = useMemo(
    () => tableData.some((item) => !pocKeyOf(item)),
    [tableData]
  );

  const filteredData = useMemo(() => {
    const query = searchText.trim().toLowerCase();
    if (!pocFilter && !query) return tableData;
    return tableData.filter((item) => {
      const name = pocKeyOf(item);
      const matchesPoc =
        !pocFilter || (pocFilter === UNASSIGNED_POC ? !name : name === pocFilter);
      // Matches either party the row can be paid to — the Influencer or the
      // Vendor column — since a tab mixes INF and VEN rows.
      const matchesSearch =
        !query ||
        [
          item?.influencer?.username,
          item?.influencer?.name,
          item?.companyName,
          item?.company?.name,
        ].some((value) => value?.toLowerCase().includes(query));
      return matchesPoc && matchesSearch;
    });
  }, [tableData, pocFilter, searchText]);

  const hasActiveFilter = Boolean(pocFilter || searchText.trim());

  const handleView = useCallback((item) => {
    setSelectedItem(item);
    setDrawerOpen(true);
  }, []);

  const handleDrawerClose = useCallback(() => {
    setSelectedItem(null);
    setDrawerOpen(false);
  }, []);

  const handleCopy = (row) => {
    let targeturl = process.env.REACT_APP_FORM_BASE_URL;

    if (row.isCompany) {
      targeturl += "/company"
    }
    else {
      targeturl += "/influencer"
    }

    targeturl += `/payment/${row.pathName}`;

    navigator.clipboard.writeText(targeturl);

    toast.success(
      'Link Copied!',
      {
        position: 'top-right'
      }
    );
  }

  if (!tableData || tableData.length === 0) {
    return (
      <Card sx={{ boxShadow: 0, p: 3 }}>
        <Box textAlign="center">
          <Typography variant="body1" color="textSecondary">
            No {statusType} records found
          </Typography>
        </Box>
      </Card>
    );
  }

  return (
    <>
      <Card sx={{ boxShadow: 0 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1.5,
            px: 1.5,
            // An outlined TextField floats its label above the input's
            // border, so without headroom the tab divider directly above
            // clips it in half.
            pt: 2.5,
            pb: 1.5,
            mb: 0.5,
            borderBottom: "1px solid",
            borderColor: "#e5e7eb",
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            sx={{ width: { xs: "100%", sm: "auto" } }}
          >
            <SearchField
              placeholder="Search by Creator"
              value={searchText}
              onChange={setSearchText}
            />

            {pocOptions.length > 0 && (
              <TextField
                select
                size="small"
                label="POC"
                value={pocFilter}
                onChange={(e) => setPocFilter(e.target.value)}
                // See the matching comment in QueueStatus.jsx: without these a
                // Select shows an empty box while "All POCs" is selected.
                InputLabelProps={{ shrink: true }}
                SelectProps={{
                  displayEmpty: true,
                  renderValue: (value) => {
                    if (!value) return "All POCs";
                    if (value === UNASSIGNED_POC) return "No POC";
                    return value;
                  },
                }}
                sx={{ width: { xs: "100%", sm: 190 }, ...filterSelectSx }}
              >
                <MenuItem value="">
                  All POCs
                </MenuItem>

                {pocOptions.map((name) => (
                  <MenuItem key={name} value={name}>
                    {name}
                  </MenuItem>
                ))}

                {hasUnassignedPoc && (
                  <MenuItem value={UNASSIGNED_POC}>
                    No POC
                  </MenuItem>
                )}
              </TextField>
            )}
          </Stack>

          {/* Only meaningful once a filter is on — otherwise the count
              would just restate the tab's own header badge. */}
          {hasActiveFilter && (
            <Stack direction="row" alignItems="center" spacing={1}>
              <Chip
                size="small"
                label={`${filteredData.length} of ${tableData.length}`}
                sx={{
                  fontFamily: "Quicksand",
                  fontWeight: 700,
                  fontSize: "11px",
                  backgroundColor: "#eef2ff",
                  color: "#3730a3",
                }}
              />
              <Button
                size="small"
                startIcon={<Close sx={{ fontSize: 14 }} />}
                onClick={() => {
                  setPocFilter("");
                  setSearchText("");
                }}
                sx={{
                  fontFamily: "Quicksand",
                  fontWeight: 700,
                  fontSize: "11px",
                  textTransform: "none",
                  color: "#6b7280",
                }}
              >
                Clear
              </Button>
            </Stack>
          )}
        </Box>

        <TableContainer>
          <Table
            aria-label="simple table"
            size="lg"
            sx={{
              '& .MuiTableCell-root': {
                padding: '4px 8px',
                borderRight: 1,
                borderRightColor: '#c0cacf'
              },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell sx={STYLING_TABLE}>Date</TableCell>
                <TableCell sx={STYLING_TABLE} align="center">Payment</TableCell>
                <TableCell sx={STYLING_TABLE}>Influencer</TableCell>
                <TableCell sx={STYLING_TABLE}>Vendor</TableCell>
                <TableCell sx={STYLING_TABLE}>POC</TableCell>
                <TableCell sx={STYLING_TABLE} align="center">Total Amount</TableCell>
                <TableCell sx={STYLING_TABLE} align="center">GST Amount</TableCell>
                <TableCell sx={STYLING_TABLE}>Percentage</TableCell>
                <TableCell sx={STYLING_TABLE} align="center">Payable Amount</TableCell>
                <TableCell sx={STYLING_TABLE} align="center">Payable Date</TableCell>
                <TableCell sx={STYLING_TABLE} align="center">Remarks</TableCell>
                <TableCell sx={STYLING_TABLE} align="center">Action</TableCell>
                <TableCell sx={STYLING_TABLE} align="center">SS</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {/* The card's early return only covers a genuinely empty tab;
                  a filter that matches nothing needs its own message here,
                  or the table would just render a bare header. */}
              {filteredData.length === 0 && (
                <TableRow>
                  <TableCell colSpan={13} align="center" sx={{ py: 3 }}>
                    <Typography variant="body2" color="textSecondary">
                      No matching records.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}

              {filteredData.map((item, index) => (
                <TableRow
                  key={item._id || index}
                  hover
                >
                  <TableCell>
                    <Badge
                      style={{
                        ...BADGE_BASE_STYLE,
                        backgroundColor: "#6c757d"
                      }}
                    >
                      {convertDate(item._id)}
                    </Badge>
                  </TableCell>

                  <TableCell
                    align="center"
                  >
                    {item.isCompany ? (
                      <Badge
                        style={{
                          ...BADGE_BASE_STYLE,
                          backgroundColor: "#dc3545"
                        }}
                      >
                        VEN
                      </Badge>
                    ) : (
                      <Badge
                        style={{
                          ...BADGE_BASE_STYLE,
                          backgroundColor: "#0D6EFD"
                        }}
                      >
                        INF
                      </Badge>
                    )}
                  </TableCell>

                  <TableCell
                    sx={{
                      ...TABLE_CELL_STYLE
                    }}
                  >
                    {
                      item?.influencer ?
                        <Badge
                          style={{
                            ...BADGE_BASE_STYLE,
                            backgroundColor: "#0D6EFD"
                          }}
                        >
                          {
                            item.influencer?.username ||
                            item.influencer?.name
                          }
                        </Badge>
                        :
                        ''
                    }
                  </TableCell>

                  <TableCell
                    sx={{ ...TABLE_CELL_STYLE }}
                  >
                    {
                      item?.companyName || item?.company?.name ?
                        <Badge
                          style={{
                            ...BADGE_BASE_STYLE,
                            backgroundColor: "#dc3545"
                          }}
                        >
                          {item?.companyName || item?.company?.name}
                        </Badge>
                        :
                        ""
                    }
                  </TableCell>

                  <TableCell
                    sx={{ ...TABLE_CELL_STYLE }}
                  >
                    {item.createdby?.fullName || "-"}
                  </TableCell>

                  <TableCell
                    align="center"
                  >
                    <Badge
                      style={{
                        ...BADGE_BASE_STYLE,
                        backgroundColor: "#198754",
                      }}
                    >
                      ₹{item.cost || "-"}
                    </Badge>
                  </TableCell>

                  <TableCell
                    align="center"
                  >
                    {
                      item.isGst ?
                        <Badge
                          style={{
                            ...BADGE_BASE_STYLE,
                            backgroundColor: "#FFC107",
                            color: "#0e1212"
                          }}
                        >
                          ₹{item.gstAmount}
                        </Badge>
                        :
                        "-"
                    }
                  </TableCell>

                  <TableCell style={{ ...TABLE_CELL_STYLE }}>
                    {item.percentage || 0}%
                  </TableCell>

                  <TableCell
                    align="center"
                  >
                    <Badge
                      style={{
                        ...BADGE_BASE_STYLE,
                        backgroundColor: "#FFC107",
                        color: "#0e1212"
                      }}
                    >
                      ₹{item.payableAmount || "-"}
                    </Badge>
                  </TableCell>

                  <TableCell align="center">
                    <Badge
                      style={{
                        ...BADGE_BASE_STYLE,
                        backgroundColor: "#6c757d"
                      }}
                    >
                      {item.payableDate ? formatPayableDate(item.payableDate) : "Not available"}
                    </Badge>
                  </TableCell>

                  <TableCell
                    sx={TABLE_CELL_STYLE}
                    align="center"
                  >
                    {item.remarks || item.payment_status || "-"}
                  </TableCell>

                  <TableCell
                    align="center"
                  >
                    <Box sx={{ display: "flex", gap: 1, alignItems: 'center', justifyContent: 'center' }}>
                      <Tooltip title="View">
                        <IconButton
                          size="small"
                          onClick={() => handleView(item)}
                        >
                          <Visibility
                            fontSize="10px"
                            htmlColor="#3c44bc"
                          />
                        </IconButton>
                      </Tooltip>

                      {/* Only show Delete & Copy Link for PENDING status */}
                      {showDeleteAndCopy(item.status, item.isDisable) && (
                        <>
                          <Tooltip title="Copy link">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleCopy(item)}
                            >
                              <ContentCopy fontSize="10px" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </Box>
                  </TableCell>

                  {item.paymentScreenshot ? (
                    <TableCell
                      align="center"
                    >
                      <Tooltip title="Download Screenshot">
                        <a
                          href={item.paymentScreenshot}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ textDecoration: "none" }}
                        >
                          <img
                            src={item.paymentScreenshot}
                            style={{
                              borderRadius: "15px",
                              height: "30px",
                              width: "30px",
                              objectFit: "contain",
                              border: "1px solid #ddd",
                              borderWidth: "2px",
                              borderColor: "red",
                            }}
                            alt="Download Screenshot"
                          />
                        </a>
                      </Tooltip>
                    </TableCell>
                  ) : (
                    <TableCell
                      align="center"
                    >
                      <Typography variant="body2" color="textSecondary">
                        -
                      </Typography>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Swipeable Drawer - Right Side */}
      <PaymentDrawer
        open={drawerOpen}
        onClose={handleDrawerClose}
        selectedItem={selectedItem}
        getPlatformNames={getPlatformNames}
      />
    </>
  );
}

export default React.memo(StatusTable);